import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  getRangeLeadSettings,
  listRangeLeads,
  sendRangeLeadEmail,
  updateRangeLead,
  updateRangeLeadSettings
} from "../lib/rangegridApi";

const FOLLOW_UP_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "not_a_fit", label: "Not a fit" },
  { value: "archived", label: "Archived" }
];

const DEFAULT_INTERESTS = [
  { value: "opening_updates", label: "Opening updates", status: "active", sortOrder: 10 },
  { value: "events_classes", label: "Events and classes", status: "active", sortOrder: 20 },
  { value: "general", label: "General question", status: "active", sortOrder: 30 }
];

const LEAD_TABS = [
  ["leads", "Leads"],
  ["templates", "Templates"],
  ["sender", "Sender"],
  ["stats", "Stats"],
  ["public-form", "Public Form"]
];

function formatDateTime(value) {
  if (!value) {
    return "Not recorded";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function formatDate(value) {
  if (!value) {
    return "No date";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function followUpLabel(value = "") {
  return FOLLOW_UP_OPTIONS.find((option) => option.value === value)?.label || "New";
}

function sourceLabel(value = "") {
  const labels = {
    codex_e2e: "Internal test",
    e2e_public_lead_capture: "Internal test",
    liberty_public_presale: "Liberty public page",
    public_range_page: "Public website",
    public_lead_form: "Public website",
    member_portal: "Member portal",
    campaign_page: "Campaign page"
  };
  const cleaned = String(value || "").trim();
  return labels[cleaned] || cleaned.replace(/[_-]+/g, " ") || "Public website";
}

function firstName(value = "") {
  return String(value || "").trim().split(/\s+/)[0] || "there";
}

function leadKey(lead = {}) {
  return lead.leadId || lead.auditId || `${lead.email}-${lead.createdAt}`;
}

function leadName(lead = {}) {
  return lead.name || lead.email || "Public lead";
}

function senderState(preferences = {}) {
  const sender = preferences?.emailSender || {};
  return {
    activeEmail: sender.activeEmail || "",
    pendingEmail: sender.pendingEmail || "",
    pendingStatus: sender.pendingStatus || "none",
    activeVerifiedAt: sender.activeVerifiedAt || "",
    lastCheckedAt: sender.lastCheckedAt || ""
  };
}

function templateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `tmpl_${crypto.randomUUID()}`;
  }
  return `tmpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function newTemplateDraft() {
  return {
    templateId: "",
    name: "Website inquiry follow-up",
    subject: "Following up from {rangeName}",
    body: `Hi {firstName},

Thanks for reaching out to {rangeName}. I wanted to follow up and help with your question about {interest}.

Best,
{rangeName}`
  };
}

function normalizeEmailTemplates(value = []) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((template) => {
      const source = template && typeof template === "object" && !Array.isArray(template) ? template : {};
      return {
        templateId: source.templateId || templateId(),
        name: source.name || source.title || "",
        subject: source.subject || "",
        body: source.body || source.content || source.message || "",
        updatedAt: source.updatedAt || ""
      };
    })
    .filter((template) => template.name && template.subject && template.body);
}

function templateTokens(lead = {}, selectedRange = {}) {
  return {
    firstName: firstName(lead.name),
    fullName: lead.name || lead.email || "there",
    email: lead.email || "",
    rangeName: selectedRange?.name || selectedRange?.profile?.displayName || "the range",
    interest: lead.interestLabel || lead.interest || "your inquiry",
    source: lead.sourceLabel || sourceLabel(lead.source),
    date: formatDate(lead.createdAt)
  };
}

function applyTemplateText(text = "", lead, selectedRange) {
  const tokens = templateTokens(lead, selectedRange);
  return Object.entries(tokens).reduce((result, [key, value]) => (
    result.replaceAll(`{${key}}`, value)
  ), String(text || ""));
}

function defaultEmailSubject(lead, selectedRange) {
  const rangeName = selectedRange?.name || "the range";
  return `Following up from ${rangeName}`;
}

function defaultEmailBody(lead, selectedRange) {
  const rangeName = selectedRange?.name || "the range";
  return `Hi ${firstName(lead?.name)},

Thanks for reaching out to ${rangeName}. I wanted to follow up and help with your question about ${lead?.interestLabel || "the range"}.

Best,`;
}

function leadContactHistory(lead = {}) {
  const source = lead && typeof lead === "object" && !Array.isArray(lead) ? lead : {};
  return Array.isArray(source.leadOutreach)
    ? source.leadOutreach
      .filter((entry) => entry && entry.type === "email")
      .sort((a, b) => new Date(b.sentAt || 0).getTime() - new Date(a.sentAt || 0).getTime())
    : [];
}

function sanitizeInterestDraft(interests = []) {
  const source = interests.length > 0 ? interests : DEFAULT_INTERESTS;
  return source.map((interest, index) => ({
    value: interest.value || "",
    label: interest.label || "",
    status: interest.status === "archived" ? "archived" : "active",
    sortOrder: Number.isFinite(Number(interest.sortOrder)) ? Number(interest.sortOrder) : (index + 1) * 10
  }));
}

function newInterestDraft() {
  return {
    value: "",
    label: "",
    status: "active",
    sortOrder: 10
  };
}

function slugifyInterest(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

function summarizeLeads(leads = []) {
  const byFollowUp = {};
  const byInterest = {};
  leads.forEach((lead) => {
    const status = lead.followUp?.status || "new";
    byFollowUp[status] = (byFollowUp[status] || 0) + 1;
    const interest = lead.interestLabel || lead.interest || "Opening updates";
    byInterest[interest] = (byInterest[interest] || 0) + 1;
  });
  const latest = leads
    .map((lead) => lead.createdAt)
    .filter(Boolean)
    .sort()
    .at(-1);
  return {
    total: leads.length,
    byFollowUp,
    byInterest,
    latest
  };
}

function percentOf(value, total) {
  if (!total) {
    return 0;
  }
  return Math.max(4, Math.round((Number(value || 0) / total) * 100));
}

export function LeadsPage({
  notificationPreferences,
  onRefreshSenderEmailVerification,
  onSaveNotificationPreferences,
  onSendLeadEmail,
  onStartSenderEmailVerification,
  savingNotificationPreferences = false,
  selectedRange,
  sendingLeadEmailId = "",
  updatingSenderEmail = false
}) {
  const [activeTab, setActiveTab] = useState(() => {
    const hash = typeof window === "undefined" ? "" : window.location.hash.replace("#leads-", "");
    return LEAD_TABS.some(([value]) => value === hash) ? hash : "leads";
  });
  const [filters, setFilters] = useState({ q: "", interest: "", followUp: "" });
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({});
  const [leadSettings, setLeadSettings] = useState({ interests: DEFAULT_INTERESTS });
  const [interestDrafts, setInterestDrafts] = useState(DEFAULT_INTERESTS);
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savingLead, setSavingLead] = useState(false);
  const [leadDraft, setLeadDraft] = useState({ status: "new", note: "" });
  const [composerOpen, setComposerOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [templateDraft, setTemplateDraft] = useState(newTemplateDraft);
  const [senderDraft, setSenderDraft] = useState("");
  const [senderSetupOpen, setSenderSetupOpen] = useState(false);
  const [senderNotice, setSenderNotice] = useState("");
  const [savingInterests, setSavingInterests] = useState(false);
  const [publicFormEditing, setPublicFormEditing] = useState(false);
  const deferredSearch = useDeferredValue(filters.q);
  const selectedLead = useMemo(
    () => leads.find((lead) => leadKey(lead) === selectedLeadId) || null,
    [leads, selectedLeadId]
  );
  const selectedLeadContactHistory = useMemo(
    () => leadContactHistory(selectedLead),
    [selectedLead]
  );
  const sender = senderState(notificationPreferences);
  const senderReady = Boolean(sender.activeEmail && sender.activeVerifiedAt);
  const senderPending = Boolean(sender.pendingEmail && sender.pendingStatus === "pending");
  const emailTemplates = useMemo(
    () => normalizeEmailTemplates(notificationPreferences?.leadEmailTemplates || []),
    [notificationPreferences]
  );
  const activeInterests = useMemo(
    () => sanitizeInterestDraft(leadSettings.interests).filter((interest) => interest.status === "active"),
    [leadSettings.interests]
  );
  const filteredLeads = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    return leads.filter((lead) => {
      if (filters.interest && lead.interest !== filters.interest) {
        return false;
      }
      if (filters.followUp && (lead.followUp?.status || "new") !== filters.followUp) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [
        lead.name,
        lead.email,
        lead.phone,
        lead.interestLabel,
        lead.message,
        lead.sourceLabel || sourceLabel(lead.source)
      ].join(" ").toLowerCase().includes(query);
    });
  }, [deferredSearch, filters.followUp, filters.interest, leads]);
  const derivedSummary = useMemo(() => summarizeLeads(leads), [leads]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#leads-${activeTab}`);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!selectedRange?.rangeId) {
      setLeads([]);
      setLeadSettings({ interests: DEFAULT_INTERESTS });
      setInterestDrafts(DEFAULT_INTERESTS);
      return;
    }

    let cancelled = false;
    async function loadLeads() {
      setLoading(true);
      setError("");
      try {
        const [leadResult, settingsResult] = await Promise.all([
          listRangeLeads(selectedRange.rangeId, { q: deferredSearch, interest: filters.interest }, 250),
          getRangeLeadSettings(selectedRange.rangeId)
        ]);
        if (!cancelled) {
          setLeads(leadResult.leads || []);
          setSummary(leadResult.summary || {});
          const nextSettings = settingsResult?.interests?.length ? settingsResult : (leadResult.settings || { interests: DEFAULT_INTERESTS });
          setLeadSettings(nextSettings);
          setInterestDrafts(sanitizeInterestDraft(nextSettings.interests || DEFAULT_INTERESTS));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLeads();
    return () => {
      cancelled = true;
    };
  }, [deferredSearch, filters.interest, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedLead) {
      setLeadDraft({ status: "new", note: "" });
      setComposerOpen(false);
      setSelectedTemplateId("");
      return;
    }
    setLeadDraft({
      status: selectedLead.followUp?.status || "new",
      note: selectedLead.followUp?.note || ""
    });
    setEmailDraft({
      subject: defaultEmailSubject(selectedLead, selectedRange),
      body: defaultEmailBody(selectedLead, selectedRange)
    });
  }, [selectedLead, selectedRange]);

  useEffect(() => {
    if (!senderDraft) {
      setSenderDraft(sender.pendingEmail || sender.activeEmail || "");
    }
  }, [sender.activeEmail, sender.pendingEmail, senderDraft]);

  function selectTab(tab) {
    setActiveTab(tab);
    setNotice("");
    setError("");
    if (tab !== "leads") {
      setSelectedLeadId("");
    }
    if (tab !== "public-form") {
      setPublicFormEditing(false);
    }
  }

  function openLead(lead) {
    setSelectedLeadId(leadKey(lead));
    setNotice("");
    setError("");
  }

  async function handleSaveFollowUp(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId || !selectedLead) {
      return;
    }
    setSavingLead(true);
    setError("");
    setNotice("");
    try {
      const updated = await updateRangeLead(selectedRange.rangeId, selectedLead.leadId || selectedLead.auditId, leadDraft);
      setLeads((current) => current.map((lead) => leadKey(lead) === selectedLeadId ? updated : lead));
      setNotice("Follow-up saved.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingLead(false);
    }
  }

  function applyTemplate(templateValue) {
    setSelectedTemplateId(templateValue);
    const template = emailTemplates.find((item) => item.templateId === templateValue);
    if (!template || !selectedLead) {
      setEmailDraft({
        subject: defaultEmailSubject(selectedLead, selectedRange),
        body: defaultEmailBody(selectedLead, selectedRange)
      });
      return;
    }
    setEmailDraft({
      subject: applyTemplateText(template.subject, selectedLead, selectedRange),
      body: applyTemplateText(template.body, selectedLead, selectedRange)
    });
  }

  async function handleSendEmail(event) {
    event.preventDefault();
    if (!selectedLead) {
      return;
    }
    setError("");
    setNotice("");
    try {
      const result = onSendLeadEmail
        ? await onSendLeadEmail(selectedLead.leadId || selectedLead.auditId, emailDraft)
        : await sendRangeLeadEmail(selectedRange.rangeId, selectedLead.leadId || selectedLead.auditId, emailDraft);
      const updated = result?.lead || result;
      if (updated?.leadId) {
        setLeads((current) => current.map((lead) => leadKey(lead) === selectedLeadId ? updated : lead));
      }
      setNotice("Email sent.");
      setComposerOpen(false);
    } catch (err) {
      setError(err.message);
    }
  }

  function startTemplateEdit(template) {
    if (!template) {
      setTemplateDraft(newTemplateDraft());
      setTemplateEditorOpen(true);
      return;
    }
    setTemplateDraft({
      templateId: template.templateId || "",
      name: template.name || "",
      subject: template.subject || "",
      body: template.body || ""
    });
    setTemplateEditorOpen(true);
  }

  async function handleSaveTemplate(event) {
    event.preventDefault();
    const name = templateDraft.name.trim();
    const subject = templateDraft.subject.trim();
    const body = templateDraft.body.trim();
    if (!name || !subject || !body) {
      setNotice("Add a template name, subject, and message.");
      return;
    }
    const nextTemplate = {
      templateId: templateDraft.templateId || templateId(),
      name,
      subject,
      body,
      updatedAt: new Date().toISOString()
    };
    const nextTemplates = emailTemplates.some((template) => template.templateId === nextTemplate.templateId)
      ? emailTemplates.map((template) => template.templateId === nextTemplate.templateId ? nextTemplate : template)
      : [nextTemplate, ...emailTemplates].slice(0, 20);
    try {
      await onSaveNotificationPreferences?.({
        ...(notificationPreferences || {}),
        leadEmailTemplates: nextTemplates
      });
      setTemplateDraft(newTemplateDraft());
      setTemplateEditorOpen(false);
      setNotice("Template saved.");
    } catch (err) {
      setError(err.message || "Template could not be saved.");
    }
  }

  async function handleDeleteTemplate(deleteTemplateId) {
    try {
      const nextTemplates = emailTemplates.filter((template) => template.templateId !== deleteTemplateId);
      await onSaveNotificationPreferences?.({
        ...(notificationPreferences || {}),
        leadEmailTemplates: nextTemplates
      });
      setNotice("Template removed.");
    } catch (err) {
      setError(err.message || "Template could not be removed.");
    }
  }

  async function handleVerifySender(event) {
    event.preventDefault();
    setSenderNotice("");
    try {
      const email = senderDraft.trim();
      const preferences = await onStartSenderEmailVerification?.(email);
      const nextSender = preferences?.emailSender || {};
      if (nextSender.activeEmail === email.toLowerCase() && nextSender.activeVerifiedAt) {
        setSenderNotice("Sender email is verified for this workspace.");
      } else {
        setSenderNotice("Verification email sent. Open that inbox and verify it, then check status here.");
      }
      setSenderSetupOpen(false);
    } catch (err) {
      setSenderNotice(err.message || "Verification could not be started.");
    }
  }

  async function handleCheckSender() {
    setSenderNotice("");
    try {
      await onRefreshSenderEmailVerification?.(sender.pendingEmail || sender.activeEmail);
      setSenderNotice("Sender status refreshed.");
    } catch (err) {
      setSenderNotice(err.message || "Sender status could not be checked.");
    }
  }

  function updateInterestDraft(index, patch) {
    setInterestDrafts((current) => current.map((interest, itemIndex) => (
      itemIndex === index ? { ...interest, ...patch } : interest
    )));
  }

  async function handleSaveInterests() {
    setSavingInterests(true);
    setError("");
    setNotice("");
    try {
      const normalized = interestDrafts
        .map((interest, index) => {
          const label = String(interest.label || "").trim();
          const value = slugifyInterest(interest.value || label);
          return label && value
            ? {
              value,
              label,
              status: interest.status === "archived" ? "archived" : "active",
              sortOrder: (index + 1) * 10
            }
            : null;
        })
        .filter(Boolean);
      const saved = await updateRangeLeadSettings(selectedRange.rangeId, { interests: normalized });
      setLeadSettings(saved);
      setInterestDrafts(sanitizeInterestDraft(saved.interests));
      setNotice("Public form saved.");
      setPublicFormEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingInterests(false);
    }
  }

  const topInterest = Object.entries(derivedSummary.byInterest || summary.byInterest || {})
    .sort((left, right) => right[1] - left[1])[0];
  const leadTotal = summary.total || derivedSummary.total || 0;
  const summaryByFollowUp = summary.byFollowUp || derivedSummary.byFollowUp || {};
  const summaryByInterest = derivedSummary.byInterest || summary.byInterest || {};
  const followUpEntries = FOLLOW_UP_OPTIONS.map((option) => ({
    ...option,
    count: summaryByFollowUp[option.value] || 0
  }));
  const interestEntries = Object.entries(summaryByInterest)
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count);
  const latestLead = [...leads]
    .filter((lead) => lead.createdAt)
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())[0];
  const trendAnchor = latestLead?.createdAt ? new Date(latestLead.createdAt) : new Date();
  const leadTrend = Array.from({ length: 7 }, (_, offset) => {
    const date = new Date(trendAnchor);
    date.setDate(trendAnchor.getDate() - (6 - offset));
    const dayKey = date.toISOString().slice(0, 10);
    return {
      key: dayKey,
      label: date.toLocaleDateString([], { month: "short", day: "numeric" }),
      count: leads.filter((lead) => String(lead.createdAt || "").slice(0, 10) === dayKey).length
    };
  });
  const maxTrendCount = Math.max(1, ...leadTrend.map((item) => item.count));
  const visibleInterestCount = interestDrafts.filter((interest) => interest.status !== "archived" && String(interest.label || "").trim()).length;
  const hiddenInterestCount = interestDrafts.filter((interest) => interest.status === "archived" && String(interest.label || "").trim()).length;

  return (
    <section className="panel page-panel leads-page">
      <div className="guest-leads-primary-tabs leads-primary-tabs" aria-label="Leads workspace">
        {LEAD_TABS.map(([value, label]) => {
          const meta = value === "leads"
            ? `${filteredLeads.length} ${filteredLeads.length === 1 ? "lead" : "leads"}`
            : value === "templates"
              ? `${emailTemplates.length} saved`
              : value === "sender"
                ? senderReady ? sender.activeEmail : senderPending ? "Pending" : "Not verified"
                : value === "public-form"
                  ? `${activeInterests.length} active`
                  : `${summary.total || derivedSummary.total || 0} total`;
          return (
            <button
              type="button"
              className={activeTab === value ? "is-active" : ""}
              key={value}
              onClick={() => selectTab(value)}
            >
              <strong>{label}</strong>
              <span>{meta}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="status-strip">Refreshing leads...</div>}
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="guest-leads-inline-notice">{notice}</div>}

      {activeTab === "leads" && !selectedLead && (
        <>
          <div className="guest-leads-heading">
            <div>
              <h3>Public leads</h3>
              <p>{filteredLeads.length} inquiries from the public website</p>
            </div>
            <span>Newest first</span>
          </div>
          <div className="guest-leads-toolbar" aria-label="Lead filters">
            <label>
              <span>Search</span>
              <input
                type="search"
                value={filters.q}
                onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
                placeholder="Name, email, note, or interest"
              />
            </label>
            <label>
              <span>Interest</span>
              <select
                value={filters.interest}
                onChange={(event) => setFilters((current) => ({ ...current, interest: event.target.value }))}
              >
                <option value="">All interests</option>
                {activeInterests.map((interest) => (
                  <option value={interest.value} key={interest.value}>{interest.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Follow-up</span>
              <select
                value={filters.followUp}
                onChange={(event) => setFilters((current) => ({ ...current, followUp: event.target.value }))}
              >
                <option value="">All follow-ups</option>
                {FOLLOW_UP_OPTIONS.map((option) => (
                  <option value={option.value} key={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
          {!loading && filteredLeads.length === 0 && (
            <div className="empty-state">No leads match this view.</div>
          )}
          {!loading && filteredLeads.length > 0 && (
            <div className="non-member-attendee-list leads-list" aria-label="Public lead list">
              {filteredLeads.map((lead) => (
                <button
                  type="button"
                  className="non-member-attendee-row leads-row"
                  key={leadKey(lead)}
                  onClick={() => openLead(lead)}
                >
                  <div>
                    <strong>{leadName(lead)}</strong>
                    <span>{lead.email || lead.phone || "No contact captured"}</span>
                  </div>
                  <div>
                    <span>{lead.interestLabel || lead.interest}</span>
                    <small>{lead.sourceLabel || sourceLabel(lead.source)}</small>
                  </div>
                  <div>
                    <span>{formatDate(lead.createdAt)}</span>
                    <small>{followUpLabel(lead.followUp?.status)}</small>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "leads" && selectedLead && (
        <div className={`leads-detail-screen${composerOpen ? " is-composing" : ""}`}>
          <section className="leads-detail-profile" aria-label="Selected lead">
            {!composerOpen && (
              <div className="leads-detail-topbar">
                <div className="leads-detail-identity">
                  <button type="button" className="guest-lead-back-link leads-back-button" onClick={() => setSelectedLeadId("")}>
                    Back to leads
                  </button>
                  <div>
                    <strong>{leadName(selectedLead)}</strong>
                    <span>{selectedLead.email || selectedLead.phone || "No contact captured"}</span>
                  </div>
                </div>
                <div className="leads-detail-actions">
                  <div className="leads-last-email">
                    <span>Last email</span>
                    <strong>{selectedLead.lastEmail?.sentAt ? formatDateTime(selectedLead.lastEmail.sentAt) : "No email sent"}</strong>
                  </div>
                  {selectedLead.email && (
                    <button
                      type="button"
                      className="primary-button leads-email-button"
                      onClick={() => setComposerOpen(true)}
                      disabled={!senderReady || sendingLeadEmailId === selectedLead.leadId}
                    >
                      {sendingLeadEmailId === selectedLead.leadId
                        ? "Sending..."
                        : senderReady ? "Email lead" : "Verify sender first"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {composerOpen ? (
              <form className="guest-lead-compose" onSubmit={handleSendEmail}>
                <div className="guest-lead-compose-heading">
                  <span>Email lead</span>
                  <strong>{leadName(selectedLead)}</strong>
                </div>
                {emailTemplates.length > 0 && (
                  <label>
                    <span>Template</span>
                    <select value={selectedTemplateId} onChange={(event) => applyTemplate(event.target.value)}>
                      <option value="">Start fresh</option>
                      {emailTemplates.map((template) => (
                        <option key={template.templateId} value={template.templateId}>{template.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  <span>Subject</span>
                  <input
                    value={emailDraft.subject}
                    onChange={(event) => setEmailDraft((current) => ({ ...current, subject: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Message</span>
                  <textarea
                    rows={6}
                    value={emailDraft.body}
                    onChange={(event) => setEmailDraft((current) => ({ ...current, body: event.target.value }))}
                  />
                </label>
                <div className="guest-lead-compose-actions">
                  <button type="button" className="secondary-button leads-secondary-action" onClick={() => setComposerOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="primary-button leads-email-button" disabled={sendingLeadEmailId === selectedLead.leadId}>
                    {sendingLeadEmailId === selectedLead.leadId ? "Sending..." : "Send email"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="leads-detail-body">
                <div className="leads-detail-main">
                  <section className="leads-detail-card">
                    <div className="leads-detail-card-heading">
                      <h3>Inquiry</h3>
                      <span>{followUpLabel(selectedLead.followUp?.status)}</span>
                    </div>
                    <div className="leads-detail-facts">
                      <div>
                        <span>Interest</span>
                        <strong>{selectedLead.interestLabel || selectedLead.interest}</strong>
                      </div>
                      <div>
                        <span>Source</span>
                        <strong>{selectedLead.sourceLabel || sourceLabel(selectedLead.source)}</strong>
                      </div>
                      <div>
                        <span>Received</span>
                        <strong>{formatDateTime(selectedLead.createdAt)}</strong>
                      </div>
                    </div>
                  </section>
                  <section className="lead-message-card">
                    <span>Visitor message</span>
                    {selectedLead.message ? (
                      <p>{selectedLead.message}</p>
                    ) : (
                      <p className="lead-message-empty">No message included with this request.</p>
                    )}
                  </section>
                  <section className="lead-contact-history-card" aria-label="Lead contact history">
                    <div className="lead-contact-history-heading">
                      <div>
                        <span>Contact history</span>
                        <strong>{selectedLeadContactHistory.length ? `${selectedLeadContactHistory.length} email${selectedLeadContactHistory.length === 1 ? "" : "s"} sent` : "No emails sent yet"}</strong>
                      </div>
                    </div>
                    {selectedLeadContactHistory.length ? (
                      <div className="lead-contact-history-list">
                        {selectedLeadContactHistory.map((entry) => (
                          <article className="lead-contact-history-item" key={entry.outreachId || `${entry.sentAt}-${entry.subject}`}>
                            <div className="lead-contact-history-time">
                              <span>{formatDateTime(entry.sentAt)}</span>
                              <small>{entry.senderEmail || "Workspace sender"}</small>
                            </div>
                            <div className="lead-contact-history-copy">
                              <strong>{entry.subject || "Email sent"}</strong>
                              <p>{entry.bodyPreview || "No preview saved."}</p>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="lead-contact-history-empty">Emails sent from this lead will appear here with the send time and message preview.</p>
                    )}
                  </section>
                </div>

                <aside className="leads-detail-side">
                  <div className="leads-contact-card">
                    <span>Contact</span>
                    <strong>{selectedLead.email || selectedLead.phone || "No contact captured"}</strong>
                    {selectedLead.phone && selectedLead.email && <small>{selectedLead.phone}</small>}
                  </div>
                  <form className="lead-follow-up-form" onSubmit={handleSaveFollowUp}>
                    <label>
                      <span>Follow-up</span>
                      <select
                        value={leadDraft.status}
                        onChange={(event) => setLeadDraft((current) => ({ ...current, status: event.target.value }))}
                      >
                        {FOLLOW_UP_OPTIONS.map((option) => (
                          <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Owner note</span>
                      <textarea
                        rows={4}
                        value={leadDraft.note}
                        onChange={(event) => setLeadDraft((current) => ({ ...current, note: event.target.value }))}
                        placeholder="Next step, call outcome, or reason this is qualified"
                      />
                    </label>
                    <button type="submit" className="primary-button leads-save-button" disabled={savingLead}>
                      {savingLead ? "Saving..." : "Save follow-up"}
                    </button>
                  </form>
                </aside>
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === "templates" && (
        <div className={`guest-leads-template-manager ${templateEditorOpen ? "is-editing" : ""}`}>
          <div className="guest-leads-template-list">
            <div className="guest-leads-template-card guest-leads-template-command">
              <button type="button" className="secondary-button" onClick={() => startTemplateEdit(null)}>
                Create template
              </button>
            </div>
            {emailTemplates.length === 0 ? (
              <div className="guest-leads-template-empty">
                <strong>No templates yet</strong>
              </div>
            ) : emailTemplates.map((template) => (
              <div className="guest-leads-template-row" key={template.templateId}>
                <div>
                  <strong>{template.name}</strong>
                  <span>{template.subject}</span>
                  <small>{String(template.body || "").replace(/\s+/g, " ").slice(0, 130)}{String(template.body || "").length > 130 ? "..." : ""}</small>
                </div>
                <div>
                  <button type="button" className="secondary-button" onClick={() => startTemplateEdit(template)}>
                    Edit
                  </button>
                  <button type="button" className="secondary-button" onClick={() => handleDeleteTemplate(template.templateId)} disabled={savingNotificationPreferences}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {templateEditorOpen && (
            <form className="guest-leads-template-form" onSubmit={handleSaveTemplate}>
              <label>
                <span>Template name</span>
                <input value={templateDraft.name} onChange={(event) => setTemplateDraft((current) => ({ ...current, name: event.target.value }))} />
              </label>
              <label>
                <span>Subject</span>
                <input value={templateDraft.subject} onChange={(event) => setTemplateDraft((current) => ({ ...current, subject: event.target.value }))} />
              </label>
              <label>
                <span>Message</span>
                <textarea rows={7} value={templateDraft.body} onChange={(event) => setTemplateDraft((current) => ({ ...current, body: event.target.value }))} />
              </label>
              <div className="guest-leads-template-actions">
                <button type="button" className="secondary-button" onClick={() => setTemplateEditorOpen(false)} disabled={savingNotificationPreferences}>
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={savingNotificationPreferences}>
                  {savingNotificationPreferences ? "Saving..." : "Save template"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {activeTab === "sender" && (
        <>
          <div className={`guest-leads-sender-card ${senderReady ? "is-ready" : ""}`}>
            <div>
              <span>Sender email</span>
              <strong>{senderReady ? sender.activeEmail : senderPending ? sender.pendingEmail : "Not verified"}</strong>
              {senderPending && <small>Verify this inbox, then return here to check status.</small>}
              {senderReady && senderPending && sender.pendingEmail !== sender.activeEmail && <small>Waiting on replacement: {sender.pendingEmail}</small>}
              {senderReady && !senderPending && <small>Public lead emails send from this workspace address.</small>}
            </div>
            {(senderReady || senderPending) && (
              <div className="guest-leads-sender-actions">
                {senderPending && (
                  <button type="button" className="secondary-button" onClick={handleCheckSender} disabled={updatingSenderEmail}>
                    {updatingSenderEmail ? "Checking..." : "Check status"}
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setSenderDraft(sender.pendingEmail || sender.activeEmail || "");
                    setSenderSetupOpen((open) => !open);
                    setSenderNotice("");
                  }}
                >
                  {senderReady ? "Replace sender" : "Change email"}
                </button>
              </div>
            )}
          </div>
          {(senderSetupOpen || (!senderReady && !senderPending)) && (
            <form className="guest-leads-sender-form" onSubmit={handleVerifySender}>
              <label>
                <span>Email to send from</span>
                <input
                  type="email"
                  value={senderDraft}
                  onChange={(event) => setSenderDraft(event.target.value)}
                  placeholder="owner@example.com"
                />
              </label>
              <button type="submit" className="primary-button" disabled={updatingSenderEmail}>
                {updatingSenderEmail ? "Sending..." : "Send verification"}
              </button>
            </form>
          )}
          {senderNotice && <div className="guest-leads-inline-notice">{senderNotice}</div>}
        </>
      )}

      {activeTab === "stats" && (
        <div className="lead-stats-workspace">
          <section className="lead-stats-hero">
            <div>
              <span>Lead flow</span>
              <strong>{leadTotal}</strong>
              <small>{leadTotal === 1 ? "public inquiry captured" : "public inquiries captured"}</small>
            </div>
            <div>
              <span>Latest lead</span>
              <strong>{latestLead ? leadName(latestLead) : "No lead yet"}</strong>
              <small>{latestLead ? formatDateTime(latestLead.createdAt) : "New public requests will appear here."}</small>
            </div>
            <div>
              <span>Top interest</span>
              <strong>{topInterest ? topInterest[0] : "No interest yet"}</strong>
              <small>{topInterest ? `${topInterest[1]} ${topInterest[1] === 1 ? "lead" : "leads"}` : "Collected from the public form."}</small>
            </div>
          </section>

          <section className="lead-stats-card lead-trend-card">
            <div className="lead-stats-card-header">
              <div>
                <h3>Recent requests</h3>
                <p>Public form activity by day</p>
              </div>
              <span>{formatDate(leadTrend[0]?.key)} - {formatDate(leadTrend.at(-1)?.key)}</span>
            </div>
            <div className="lead-trend-bars" aria-label="Recent public lead requests">
              {leadTrend.map((item) => (
                <div className="lead-trend-day" key={item.key}>
                  <div>
                    <span style={{ height: `${Math.max(8, Math.round((item.count / maxTrendCount) * 100))}%` }} />
                  </div>
                  <strong>{item.count}</strong>
                  <small>{item.label}</small>
                </div>
              ))}
            </div>
          </section>

          <div className="lead-stats-grid">
            <section className="lead-stats-card">
              <div className="lead-stats-card-header">
                <div>
                  <h3>Follow-up</h3>
                  <p>Where each lead sits now</p>
                </div>
              </div>
              <div className="lead-breakdown-list">
                {followUpEntries.map((entry) => (
                  <div className={entry.count ? "" : "is-empty"} key={entry.value}>
                    <div>
                      <span>{entry.label}</span>
                      <strong>{entry.count}</strong>
                    </div>
                    <em><span style={{ width: `${percentOf(entry.count, leadTotal)}%` }} /></em>
                  </div>
                ))}
              </div>
            </section>

            <section className="lead-stats-card">
              <div className="lead-stats-card-header">
                <div>
                  <h3>Interests</h3>
                  <p>What people ask about</p>
                </div>
              </div>
              {interestEntries.length === 0 ? (
                <div className="lead-stats-empty">No interests captured yet.</div>
              ) : (
                <div className="lead-breakdown-list">
                  {interestEntries.map((entry) => (
                    <div key={entry.label}>
                      <div>
                        <span>{entry.label}</span>
                        <strong>{entry.count}</strong>
                      </div>
                      <em><span style={{ width: `${percentOf(entry.count, leadTotal)}%` }} /></em>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      )}

      {activeTab === "public-form" && (
        <div className="lead-public-form-settings">
          <div className="lead-public-form-header">
            <div>
              <h3>Public lead form</h3>
              <p>Choose the options visitors can select when they contact the range.</p>
            </div>
            <div className="lead-public-form-counts">
              <span>{visibleInterestCount} visible</span>
              {hiddenInterestCount > 0 && <span>{hiddenInterestCount} hidden</span>}
              {!publicFormEditing && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setPublicFormEditing(true)}
                >
                  Edit form
                </button>
              )}
            </div>
          </div>

          <div className={`lead-public-form-layout${publicFormEditing ? " is-editing" : ""}`}>
            <section className="lead-public-form-preview" aria-label="Public form preview">
              <span>Public preview</span>
              <strong>What are you interested in?</strong>
              <div className="lead-public-select-preview">
                {interestDrafts
                  .filter((interest) => interest.status !== "archived" && String(interest.label || "").trim())
                  .map((interest) => (
                    <div key={interest.value || interest.label}>{interest.label}</div>
                  ))}
                {visibleInterestCount === 0 && <div>No visible options</div>}
              </div>
            </section>

            {publicFormEditing ? (
              <section className="lead-interest-editor" aria-label="Lead interest options">
                <div className="lead-interest-editor-toolbar">
                  <strong>Interest options</strong>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setInterestDrafts((current) => [...current, newInterestDraft()])}
                  >
                    Add option
                  </button>
                </div>
                <div className="lead-interest-list">
                  {interestDrafts.map((interest, index) => (
                    <div className={`lead-interest-row${interest.status === "archived" ? " is-hidden" : ""}`} key={`${interest.value || "new"}-${index}`}>
                      <label>
                        <span>Option label</span>
                        <input
                          value={interest.label}
                          onChange={(event) => {
                            const label = event.target.value;
                            updateInterestDraft(index, {
                              label,
                              value: interest.value || slugifyInterest(label)
                            });
                          }}
                          placeholder="Opening updates"
                        />
                      </label>
                      <label>
                        <span>Public form</span>
                        <select value={interest.status} onChange={(event) => updateInterestDraft(index, { status: event.target.value })}>
                          <option value="active">Visible</option>
                          <option value="archived">Hidden</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => setInterestDrafts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ) : (
              <section className="lead-public-form-summary" aria-label="Current public form options">
                <div className="lead-interest-editor-toolbar">
                  <strong>Visible options</strong>
                </div>
                <div className="lead-public-form-option-list">
                  {interestDrafts
                    .filter((interest) => interest.status !== "archived" && String(interest.label || "").trim())
                    .map((interest) => (
                      <div key={interest.value || interest.label}>
                        <strong>{interest.label}</strong>
                        <span>Shown on the public lead form</span>
                      </div>
                    ))}
                  {visibleInterestCount === 0 && (
                    <div>
                      <strong>No visible options</strong>
                      <span>Visitors will not have an interest choice until one is enabled.</span>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {publicFormEditing && (
            <div className="lead-settings-actions">
              <div>
                <strong>Editing public form</strong>
                <span>Changes update the interest choices on the public website.</span>
              </div>
              <div className="lead-settings-action-buttons">
                <button
                  type="button"
                  className="lead-settings-cancel-button"
                  onClick={() => {
                    setInterestDrafts(sanitizeInterestDraft(leadSettings.interests || DEFAULT_INTERESTS));
                    setPublicFormEditing(false);
                  }}
                  disabled={savingInterests}
                >
                  Cancel
                </button>
                <button type="button" className="lead-settings-save-button" onClick={handleSaveInterests} disabled={savingInterests}>
                  {savingInterests ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
